'use client';

import { MediaBlock } from '@onclicks/sharedcomponents/defaultTheme/blocks/MediaBlock';
import {
  DefaultNodeTypes,
  SerializedBlockNode,
  SerializedLinkNode,
  type DefaultTypedEditorState,
} from '@payloadcms/richtext-lexical';
import {
  JSXConvertersFunction,
  LinkJSXConverter,
  RichText as ConvertRichText,
} from '@payloadcms/richtext-lexical/react';

import { CodeBlock } from '@onclicks/sharedcomponents/defaultTheme/blocks/CodeBlock';

import type {
  BannerBlock as BannerBlockProps,
  CallToActionBlock as CTABlockProps,
  MediaBlock as MediaBlockProps,
  CodeBlock as CodeBlockProps,
} from '@/payload-types';
import { BannerBlock } from '@onclicks/sharedcomponents/defaultTheme/blocks/BannerBlock';
import { CallToActionBlock } from '../../blocks/CallToActionBlock';
import { cn } from '@onclicks/sharedcomponents/ui';
import { useLocalityService } from '@/providers/Locality';

type NodeTypes =
  | DefaultNodeTypes
  | SerializedBlockNode<
      CTABlockProps | MediaBlockProps | BannerBlockProps | CodeBlockProps
    >;

const internalDocToHref = ({ linkNode }: { linkNode: SerializedLinkNode }) => {
  const { value, relationTo } = linkNode.fields.doc!;
  if (typeof value !== 'object') {
    throw new Error('Expected value to be an object');
  }
  const slug = value.slug;
  return relationTo === 'posts' ? `/blogs/${slug}` : `/${slug}`;
};

const jsxConverters: JSXConvertersFunction<NodeTypes> = ({
  defaultConverters,
}) => ({
  ...defaultConverters,
  ...LinkJSXConverter({ internalDocToHref }),
  blocks: {
    banner: ({ node }) => (
      <BannerBlock className="col-start-2 mb-4" {...node.fields} />
    ),
    mediaBlock: ({ node }) => (
      <MediaBlock
        className="col-span-3 col-start-1"
        imgClassName="m-0"
        {...node.fields}
        captionClassName="mx-auto max-w-7xl "
        enableGutter={false}
        disableInnerContainer={true}
      />
    ),
    code: ({ node }) => <CodeBlock className="col-start-2" {...node.fields} />,
    cta: ({ node }) => <CallToActionBlock {...node.fields} />,
  },
});

type Props = {
  data: DefaultTypedEditorState;
  enableGutter?: boolean;
  enableProse?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

export default function RichText(props: Props) {
  const localityService = useLocalityService();

  const {
    className,
    enableProse = false,
    enableGutter = false,
    ...rest
  } = props;

  // On ne mute jamais `rest.data` : c'est l'objet Payload tel que resolu cote
  // serveur, potentiellement partage entre plusieurs rendus (le richtext du
  // document "pays" est le meme pour toutes ses villes). Le muter en place
  // desynchronise le HTML serveur du rendu client des qu'une autre passe de
  // rendu lit le meme objet — d'ou une hydration error.
  const data: DefaultTypedEditorState = rest.data.root.children
    ? {
        ...rest.data,
        root: {
          ...rest.data.root,
          children: rest.data.root.children.map((child) => {
            if ('children' in child && Array.isArray(child.children)) {
              return {
                ...child,
                children: child.children.map((node) =>
                  node.type === 'text' && 'text' in node
                    ? { ...node, text: localityService.replaceTags(node.text) }
                    : node,
                ),
              };
            }
            return child;
          }),
        },
      }
    : rest.data;

  return (
    <ConvertRichText
      converters={jsxConverters}
      className={cn(
        'payload-richtext',
        {
          container: enableGutter,
          'max-w-none': !enableGutter,
          'prose md:prose-md dark:prose-invert mx-auto': enableProse,
        },
        className,
      )}
      {...rest}
      data={data}
    />
  );
}
